import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatLongDate,
  formatMediumDate,
  formatNumber,
  formatPercent,
  formatShortCurrency,
  formatShortNumber,
  formatTime,
} from '@shared/utilities';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import frCA from './locales/fr-CA.json';

const registerFormatters = () => {
  const { formatter } = i18next.services;
  if (!formatter) {
    console.error('i18next formatter service unavailable; interpolation formats will not work');
    return;
  }
  formatter.add('number', (value) => formatNumber(Number(value), i18next));
  formatter.add('shortNumber', (value) => formatShortNumber(Number(value), i18next));
  formatter.add('currency', (value) => formatCurrency(Number(value), i18next));
  formatter.add('preciseCurrency', (value) => formatCurrency(Number(value), i18next, 4));
  formatter.add('shortCurrency', (value) => formatShortCurrency(Number(value), i18next));
  formatter.add('percent', (value) => formatPercent(Number(value), i18next));
  formatter.add('date', (value) => formatDate(value, i18next));
  formatter.add('longDate', (value) => formatLongDate(value, i18next));
  formatter.add('mediumDate', (value) => formatMediumDate(value, i18next));
  formatter.add('datetime', (value) => formatDateTime(value, i18next));
  formatter.add('time', (value) => formatTime(value, i18next));
};

const i18NextLanguageStorageKey = 'i18nextLng';

const clearCachedLanguage = (): void => {
  try {
    localStorage.removeItem(i18NextLanguageStorageKey);
  } catch (error: unknown) {
    console.error('Error clearing cached i18n language:', error);
  }
};

export const normalizeDetectedLanguage = (language: string): string => {
  if (language.startsWith('fr')) {
    return 'fr-CA';
  }
  return 'en';
};

export const initializeI18n = async (
  enableCanadianAdaptations: boolean,
): Promise<void> => {
  // Clear any cached language to ensure proper detection
  clearCachedLanguage();

  if (enableCanadianAdaptations) {
    await i18next
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources: {
          en: {
            translation: en,
          },
          'fr-CA': {
            translation: frCA,
          },
        },
        fallbackLng: 'en',
        supportedLngs: ['en', 'fr-CA'],
        detection: {
          convertDetectedLanguage: normalizeDetectedLanguage,
          order: ['navigator'],
          caches: [],
        },
        interpolation: {
          escapeValue: false,
        },
      });
    registerFormatters();
  } else {
    // Only English when Canadian Adaptations is disabled
    await i18next
      .use(initReactI18next)
      .init({
        resources: {
          en: {
            translation: en,
          },
        },
        lng: 'en',
        fallbackLng: 'en',
        supportedLngs: ['en'],
        interpolation: {
          escapeValue: false,
        },
      });
    registerFormatters();
  }
};

export const getTranslateFunction = (language: string) => i18next.getFixedT(language);

